import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

import { EmptyCard, QueryErrorState, TabHeading, TabSkeleton } from "../components/tab-states";
import type { TacticExperiment } from "../data/cockpit-data";
import { marketplaceCodeLabel } from "../data/naming";
import { useTacticExperiments } from "../data/queries";
import { runningTacticExperiments, tacticExperimentPresentation } from "../data/tactic-timeline";

/**
 * The tactic trials that are running right now.
 *
 * Durations and confidence come from the backend UNRECALCULATED — this surface
 * displays a verdict, it does not compute one. A frontend that re-derived
 * statistical confidence would be a second opinion nobody asked for and nobody
 * could reconcile.
 */
export function ExperimentsTab() {
  const experiments = useTacticExperiments();
  const rows = runningTacticExperiments(experiments.data ?? []);

  return (
    <div className="flex flex-col gap-4">
      <TabHeading
        title="Experiments"
        description="Running tactic trials and their backend verdict state. Durations and confidence are shown without recalculation."
        aside={!experiments.isPending && !experiments.isError ? `${rows.length} running` : undefined}
      />
      {experiments.isPending ? (
        <TabSkeleton />
      ) : experiments.isError ? (
        <QueryErrorState title="Experiments unavailable" onRetry={() => experiments.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyCard label="No tactics under test" />
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((experiment) => (
            <ExperimentRow key={experiment.experiment_id} experiment={experiment} />
          ))}
        </div>
      )}
    </div>
  );
}

function ExperimentRow({ experiment }: { experiment: TacticExperiment }) {
  const values = tacticExperimentPresentation(experiment);
  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-[15px] font-semibold text-ks-ink">{experiment.keyword}</h3>
              <Badge
                variant={
                  experiment.result === "PASS"
                    ? "secondary"
                    : experiment.result === "FAIL"
                      ? "destructive"
                      : "outline"
                }
              >
                {experiment.result}
              </Badge>
              {/* Maturity is a fact about whether the verdict can be trusted yet,
                  so it is named rather than tinted. */}
              <Badge variant="outline">{experiment.matured ? "Matured" : "Pending maturity"}</Badge>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {marketplaceCodeLabel(experiment.market)}
              {experiment.asin ? ` × ${experiment.asin}` : ""} · tactic {experiment.tactic_id}
            </p>
          </div>
          <span className="shrink-0 font-mono text-xs text-muted-foreground" title="Experiment ID">
            {values.experimentId}
          </span>
        </div>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <ExperimentMetric
            label="Time under test"
            value={`${displayValue(values.timeUnderTest)}${values.timeUnderTestUnit ? ` ${values.timeUnderTestUnit}` : ""}`}
          />
          <ExperimentMetric label="Confidence" value={displayValue(values.confidence)} />
          <ExperimentMetric label="Treatment" value={experiment.treatment ?? "unavailable"} />
          <ExperimentMetric label="Trial state" value={experiment.state} />
        </dl>
      </CardContent>
    </Card>
  );
}

function ExperimentMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-ks-grey-3 bg-muted/40 px-3 py-2">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 break-words text-sm font-semibold tabular-nums text-ks-ink">{value}</dd>
    </div>
  );
}

function displayValue(value: string | number | null): string {
  return value === null ? "unavailable" : String(value);
}
